// src/styles/designTokens.js
// Shared visual language for the connections/circles/hangouts surface
// (Connections, ConnectViaLink, Circles), so these newer pages read as one
// consistent system instead of each reinventing its own card/button/type
// scale. Built from the brand purple already used sitewide (Footer,
// EditProfile's invite card), not a new hue - a fuller tint/shade ramp of
// it, plus a small type scale and shadow/radius scale.

export const colors = {
  // Brand purple ramp
  primary: "#8B5FBF",
  primaryDark: "#6B46C1",
  primaryDarker: "#553396",
  primaryTint: "#F5F2FF",
  primaryTintBorder: "rgba(139, 95, 191, 0.18)",
  gradientPrimary: "linear-gradient(135deg, #8B5FBF 0%, #6B46C1 100%)",
  gradientTint: "linear-gradient(135deg, #FBFAFF 0%, #F5F2FF 100%)",

  // Neutrals
  ink: "#111827",
  body: "#374151",
  muted: "#6B7280",
  faint: "#9CA3AF",
  border: "#E7E5F0",
  surface: "#FFFFFF",
  surfaceMuted: "#FAFAFC",

  // Semantic
  success: "#15803D",
  successTint: "#F0FDF4",
  successBorder: "rgba(16, 185, 129, 0.3)",
  warning: "#B45309",
  warningTint: "#FFFBEB",
  warningBorder: "#F59E0B",
  danger: "#B91C1C",
};

export const type = {
  eyebrow: {
    color: colors.primary,
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  h1: { color: colors.ink, fontWeight: 800, fontSize: "1.85rem", lineHeight: 1.2, letterSpacing: "-0.01em" },
  h2: { color: colors.ink, fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.3 },
  body: { color: colors.body, fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.6 },
  bodyMuted: { color: colors.muted, fontWeight: 400, fontSize: "0.9rem", lineHeight: 1.6 },
  small: { color: colors.faint, fontWeight: 400, fontSize: "0.8rem", lineHeight: 1.5 },
};

export const radii = { sm: "10px", md: "14px", lg: "20px", pill: "999px" };

export const shadow = {
  card: "0 1px 2px rgba(17, 24, 39, 0.04), 0 8px 24px rgba(17, 24, 39, 0.05)",
  raised: "0 4px 14px rgba(139, 95, 191, 0.28)",
  raisedHover: "0 8px 22px rgba(139, 95, 191, 0.36)",
};

export const buttonPrimary = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  background: colors.gradientPrimary,
  color: "#fff",
  border: "none",
  borderRadius: radii.pill,
  padding: "13px 26px",
  fontWeight: 700,
  fontSize: "0.95rem",
  textDecoration: "none",
  cursor: "pointer",
  boxShadow: shadow.raised,
  transition: "transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
};

export const buttonSecondary = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  backgroundColor: colors.surface,
  color: colors.primaryDark,
  border: `1.5px solid ${colors.primary}`,
  borderRadius: radii.pill,
  padding: "11px 24px",
  fontWeight: 700,
  fontSize: "0.9rem",
  textDecoration: "none",
  cursor: "pointer",
  transition: "background-color 0.15s ease, transform 0.15s ease",
};

export const cardStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  padding: "22px 24px",
  boxShadow: shadow.card,
};

/** Hover handlers for buttonPrimary - spread onto a button/link as onMouseEnter/onMouseLeave. */
export const primaryHover = {
  onMouseEnter: (e) => {
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow = shadow.raisedHover;
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = shadow.raised;
  },
};

const AVATAR_PALETTE = ["#8B5FBF", "#6B46C1", "#128C4A", "#B45309", "#2563EB", "#BE185D"];

/** Deterministic accent color for an initials avatar, keyed off a name/id so the same person is always the same color. */
export function avatarColorFor(seed) {
  const str = String(seed || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}
